
import { ProxyFetchOptions, ProxyFetchResponse, ServiceType } from './types';
import { externalServicesConfig, isProxyEnabledSync, getServiceUrlSync, getProxyHeadersSync } from '@/config/proxy';
import { detectService, extractMakeWebhookPath, isMakeWebhookUrl } from './serviceDetection';

/**
 * Универсальная функция для выполнения HTTP-запросов с поддержкой проксирования
 */
export async function proxyFetch<T = any>(
  url: string,
  options: ProxyFetchOptions = {}
): Promise<ProxyFetchResponse<T>> {
  const {
    service: explicitService,
    skipProxy = false,
    retryAttempts = 3,
    timeout = 30000,
    mode,
    ...fetchOptions
  } = options;

  // Определяем сервис автоматически если не указан
  const service = explicitService || detectService(url);
  
  // Если не удалось определить сервис или прокси отключен, используем прямое соединение
  const shouldUseProxy = service && !skipProxy && isProxyEnabledSync(service);
  
  let finalUrl = url;
  let finalHeaders = { ...fetchOptions.headers };
  
  if (shouldUseProxy) {
    try {
      const proxyUrl = getServiceUrlSync(service);
      const proxyHeaders = getProxyHeadersSync(service);
      
      console.log(`🔄 Настройка прокси для ${service}:`, {
        originalUrl: url,
        proxyUrl,
        proxyHeaders,
        service
      });
      
      // Правильно формируем URL в зависимости от типа сервиса
      if (service === 'supabase' || service === 'supabaseStorage') {
        // Для Supabase заменяем базовый URL на прокси
        const serviceConfig = externalServicesConfig[service];
        let originalBaseUrl;
        
        if ('url' in serviceConfig) {
          originalBaseUrl = serviceConfig.url;
        } else if ('baseUrl' in serviceConfig) {
          originalBaseUrl = serviceConfig.baseUrl;
        } else {
          originalBaseUrl = 'https://urwzhudyitjovstzqfdj.supabase.co';
        }
        
        // Заменяем базовый URL на прокси URL
        finalUrl = url.replace(originalBaseUrl, proxyUrl);
        
        // Сохраняем исходные заголовки Supabase и добавляем прокси заголовки
        finalHeaders = {
          ...finalHeaders,
          ...proxyHeaders
        };
        
        console.log(`🔄 Supabase URL трансформация: ${url} -> ${finalUrl}`);
        
      } else if (service === 'makeWebhooks' && isMakeWebhookUrl(url)) {
        // Для Make.com webhooks извлекаем путь и добавляем к прокси URL
        const webhookPath = extractMakeWebhookPath(url);
        
        if (webhookPath) {
          // Создаем новый URL: прокси база + путь webhook
          finalUrl = `${proxyUrl.replace(/\/$/, '')}${webhookPath}`;
        } else {
          // Fallback к старому методу если не удалось извлечь путь
          finalUrl = proxyUrl;
          finalHeaders['X-Original-URL'] = url;
        }
        
        finalHeaders = {
          ...finalHeaders,
          ...proxyHeaders,
          'X-Proxy-Target': 'make-webhook',
          'Content-Type': 'application/json'
        };
        
        console.log(`🔄 Make.com webhook проксирование с путем: ${url} -> ${finalUrl}`);
        
      } else {
        // Для других внешних сервисов (Google Maps, etc.)
        finalUrl = `${proxyUrl}?target=${encodeURIComponent(url)}`;
        finalHeaders = {
          ...finalHeaders,
          ...proxyHeaders,
          'X-Original-URL': url
        };
        
        console.log(`🔄 Внешний сервис проксирование: ${url} -> ${finalUrl}`);
      }
      
    } catch (error) {
      console.warn(`⚠️ Ошибка настройки прокси для ${service}, используется прямое соединение:`, error);
      // В случае ошибки используем оригинальный URL
      finalUrl = url;
    }
  } else {
    console.log(`🌐 Прямое соединение для ${service || 'неизвестный сервис'}: ${url}`);
  }

  // Выполняем запрос с повторными попытками
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= retryAttempts; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      console.log(`🌐 Запрос (попытка ${attempt}): ${finalUrl}`, {
        method: fetchOptions.method || 'GET',
        headers: Object.keys(finalHeaders),
        service,
        proxyUsed: shouldUseProxy,
        mode: mode || 'cors'
      });
      
      const response = await fetch(finalUrl, {
        ...fetchOptions,
        headers: finalHeaders,
        signal: controller.signal,
        mode: mode || 'cors' // Используем переданный mode или 'cors' по умолчанию
      });
      
      clearTimeout(timeoutId);
      
      let data: T | undefined;
      const contentType = response.headers.get('content-type');
      
      // Для режима no-cors response будет opaque и мы не сможем читать данные
      if (mode !== 'no-cors' && contentType?.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          console.warn('Не удалось распарсить JSON ответ:', jsonError);
        }
      }
      
      console.log(`📊 Ответ от ${service || 'неизвестный'}: статус ${response.status}`, {
        ok: response.ok,
        contentType,
        url: finalUrl,
        mode: mode || 'cors'
      });
      
      return {
        data,
        status: response.status,
        ok: response.ok,
        error: response.ok ? undefined : `HTTP ${response.status}: ${response.statusText}`
      };
      
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.warn(`⚠️ Ошибка запроса ${finalUrl} (попытка ${attempt}):`, {
        error: lastError.message,
        service,
        proxyUsed: shouldUseProxy,
        mode: mode || 'cors'
      });
      
      if (attempt < retryAttempts) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        console.log(`🔄 Повторная попытка через ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  return {
    status: 0,
    ok: false,
    error: lastError?.message || 'Неизвестная ошибка сети'
  };
}
